import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as model from "../../../models/ClientBranchesModel";
import { apiError } from "../../../helpers/ErrorHelper";
import { useAuth } from "../../../context/AuthContext";
import { MySelect, Text, TextArea } from "../../inputs/inputs";
import { getStates } from "../../../models/MasterModel";
import { checkPermission } from "../../../helpers/GeneralHelper";

const emptyBranch = {
  client_branch_name: "",
  client_branch_gstin: "",
  client_branch_address: "",
  client_branch_billing_state: "",
  client_branch_pincode: "",
  client_branch_state_other: "",
  client_branch_state_code: "",
  client_branch_is_default: "0",
};

const yesNoOptions = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
];

export default function ClientBranches({
  clientId,
  isView = false,
  moduleRoute = "clients",
}) {
  const { state } = useAuth();
  const user_data = state.user_data;
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState(emptyBranch);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState("");
  const [statesList, setStatesList] = useState([]);

  const canEdit = !isView && checkPermission(moduleRoute, "edit");

  const loadBranches = async ({ autoCreate = false } = {}) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await model.getData({
        user_token: user_data.user_token,
        client_branch_client_id: clientId,
        page: 1,
        limit: -1,
        ...(autoCreate ? { auto_create: "1" } : {}),
      });
      if (autoCreate && response?.branch_auto_created) {
        toast.info("Default billing branch was created from client details.");
      }
      setBranches(response?.client_branches || []);
    } catch (error) {
      console.error(error);
      setBranches([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadBranches({ autoCreate: true });
  }, [clientId]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await getStates({
          user_token: user_data.user_token,
          page: 1,
          limit: -1,
          _country_id: 99,
        });
        if (!result?.states) return;
        setStatesList(
          result.states.map((item) => ({
            value: String(item._state_id),
            label: item.state_name,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchStates();
  }, []);

  const resetForm = () => {
    setFields(emptyBranch);
    setErrors({});
    setEditId("");
  };

  const startEdit = (row) => {
    setEditId(row._client_branch_id);
    setFields({
      client_branch_name: row.client_branch_name || "",
      client_branch_gstin: row.client_branch_gstin || "",
      client_branch_address: row.client_branch_address || "",
      client_branch_billing_state: row.client_branch_billing_state
        ? String(row.client_branch_billing_state)
        : "",
      client_branch_pincode: row.client_branch_pincode || "",
      client_branch_state_other: row.client_branch_state_other || "",
      client_branch_state_code: row.client_branch_state_code || "",
      client_branch_is_default: String(row.client_branch_is_default ?? "0"),
    });
    setErrors({});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!fields.client_branch_name?.trim()) {
      setErrors({ client_branch_name: "Branch name is required" });
      toast.error("Please enter a branch name");
      return;
    }

    setSaving(true);
    try {
      const formData = {
        client_branch_client_id: clientId,
        client_branch_name: fields.client_branch_name,
        client_branch_gstin: fields.client_branch_gstin,
        client_branch_address: fields.client_branch_address,
        client_branch_billing_state: fields.client_branch_billing_state || "",
        client_branch_pincode: fields.client_branch_pincode,
        client_branch_state_other: fields.client_branch_state_other,
        client_branch_state_code: fields.client_branch_state_code,
        client_branch_is_default: fields.client_branch_is_default || "0",
      };

      let response;
      if (editId) {
        response = await model.update(user_data.user_token, editId, formData);
      } else {
        response = await model.create(user_data.user_token, formData);
      }

      if (response.status) {
        toast.success(response.message);
        resetForm();
        loadBranches({ autoCreate: false });
      } else {
        setErrors(response.error || {});
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
    }
    setSaving(false);
  };

  const removeRow = async (row) => {
    if (!window.confirm("Delete this branch?")) return;
    try {
      const response = await model.remove(
        user_data.user_token,
        row._client_branch_id
      );
      if (response.status) {
        toast.success(response.message);
        loadBranches({ autoCreate: false });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
    }
  };

  if (!clientId) {
    return (
      <p className="text-muted mb-0">
        Save the client first to manage branches.
      </p>
    );
  }

  return (
    <div>
      {canEdit && (
        <form onSubmit={submit} className="mb-4">
          <div className="row gy-2">
            <div className="col-xl-4 mb-2">
              <Text
                column="client_branch_name"
                label="Branch Name"
                required={true}
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <Text
                column="client_branch_gstin"
                label="GSTIN"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <MySelect
                column="client_branch_is_default"
                label="Default Branch"
                options={yesNoOptions}
                firstOption={false}
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <MySelect
                column="client_branch_billing_state"
                label="Billing State"
                options={statesList}
                firstOption="Select state"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <Text
                column="client_branch_pincode"
                label="Pincode"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <Text
                column="client_branch_state_code"
                label="State Code"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-6 mb-2">
              <Text
                column="client_branch_state_other"
                label="State (Other)"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-6 mb-2">
              <TextArea
                column="client_branch_address"
                label="Address"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-12">
              <button className="btn btn-primary btn-sm me-2" disabled={saving}>
                {saving
                  ? "Saving..."
                  : editId
                    ? "Update Branch"
                    : "Add Branch"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-muted">Loading branches...</p>
      ) : branches.length === 0 ? (
        <p className="text-muted mb-0">No branches yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Branch</th>
                <th>GSTIN</th>
                <th>State</th>
                <th>Pincode</th>
                <th>Default</th>
                {canEdit && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {branches.map((row, index) => (
                <tr key={row._client_branch_id || index}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{row.client_branch_name}</div>
                    <small className="text-muted">
                      {row.client_branch_address || "-"}
                    </small>
                  </td>
                  <td>{row.client_branch_gstin || "-"}</td>
                  <td>
                    {row.state_name ||
                      row.client_branch_state_other ||
                      "-"}
                  </td>
                  <td>{row.client_branch_pincode || "-"}</td>
                  <td>
                    {Number(row.client_branch_is_default) === 1 ? (
                      <span className="badge bg-success-transparent">Yes</span>
                    ) : (
                      "No"
                    )}
                  </td>
                  {canEdit && (
                    <td>
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-info"
                          title="Edit"
                          onClick={() => startEdit(row)}
                        >
                          <i className="bx bxs-edit"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-danger"
                          title="Delete"
                          onClick={() => removeRow(row)}
                        >
                          <i className="bx bxs-trash"></i>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
